/**
 * Atlas PM · 操作日志视图
 *
 * 全量操作流（分页）+ 四维筛选（动作 / 资源类型 / 操作人 / 时间范围）
 * + ⭐ 哈希链完整性校验状态。
 *
 * ⚠️ 数据量：audit_log 当前 3000+ 条且持续增长，故：
 *     - 服务端 offset 分页（每页 20 条）；
 *     - 「是否还有下一页」用 limit = PAGE_SIZE + 1 探测，避免多请求；
 *     - 资源类型筛选大小写不敏感（后端 LOWER()），前端一律传小写。
 */

import { useMemo, useState } from 'react';
import Badge from '../../components/Badge';
import Button from '../../components/Button';
import Panel from '../../components/Panel';
import {
  ACTION_OPTIONS,
  RESOURCE_TYPE_OPTIONS,
  actionLabel,
  modeLabel,
  resourceLabel,
  type AuditLogRecord,
  type AuditQueryParams,
} from './api';
import { useAuditLogs, useAuditVerify } from './useAuditLogs';
import './audit.css';

const PAGE_SIZE = 20;

interface FilterDraft {
  action: string;
  resourceType: string;
  actorId: string;
  from: string;
  to: string;
}

const EMPTY_FILTER: FilterDraft = {
  action: '',
  resourceType: '',
  actorId: '',
  from: '',
  to: '',
};

/* ── 格式化工具 ── */

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

/** ISO → 本地时间 yyyy-MM-dd HH:mm:ss */
function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

/** 日期（yyyy-MM-dd）→ 当日 00:00:00 本地时间的 ISO */
function startOfDay(dateStr: string): string {
  const [y = 1970, m = 1, d = 1] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d, 0, 0, 0, 0).toISOString();
}

/** 日期（yyyy-MM-dd）→ 当日 23:59:59.999 本地时间的 ISO */
function endOfDay(dateStr: string): string {
  const [y = 1970, m = 1, d = 1] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d, 23, 59, 59, 999).toISOString();
}

function fmtVal(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'boolean') return v ? '是' : '否';
  if (typeof v === 'object') {
    const s = JSON.stringify(v);
    return s.length > 80 ? `${s.slice(0, 80)}…` : s;
  }
  return String(v);
}

/** 把结构化 diff 渲染成人类可读摘要 */
function renderDiff(diff: AuditLogRecord['diff']): string {
  if (!diff || typeof diff !== 'object') return '';
  const parts: string[] = [];
  for (const [key, change] of Object.entries(diff)) {
    const c = (change ?? {}) as { from?: unknown; to?: unknown };
    if (c.from === undefined && c.to === undefined) continue;
    if (c.from === undefined) parts.push(`${key} ${fmtVal(c.to)}`);
    else if (c.to === undefined) parts.push(`${key} 移除`);
    else parts.push(`${key} ${fmtVal(c.from)} → ${fmtVal(c.to)}`);
    if (parts.length >= 3) break;
  }
  return parts.join('；');
}

function shortHash(h: string | null | undefined): string {
  if (!h) return '—';
  return `${h.slice(0, 12)}…`;
}

/* ── 组件 ── */

export default function AuditLogView() {
  const [draft, setDraft] = useState<FilterDraft>(EMPTY_FILTER);
  const [applied, setApplied] = useState<FilterDraft>(EMPTY_FILTER);
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const verify = useAuditVerify();

  const params = useMemo<AuditQueryParams>(
    () => ({
      action: applied.action || undefined,
      resourceType: applied.resourceType || undefined,
      actorId: applied.actorId.trim() || undefined,
      from: applied.from ? startOfDay(applied.from) : undefined,
      to: applied.to ? endOfDay(applied.to) : undefined,
      // ⭐ +1 探测：多取一条即可判断是否还有下一页，无需额外 count 请求
      limit: PAGE_SIZE + 1,
      offset: (page - 1) * PAGE_SIZE,
    }),
    [applied, page],
  );

  const logs = useAuditLogs(params);

  const raw = logs.data?.items ?? [];
  const hasNext = raw.length > PAGE_SIZE;
  const rows = hasNext ? raw.slice(0, PAGE_SIZE) : raw;

  const hasFilter =
    applied.action !== '' ||
    applied.resourceType !== '' ||
    applied.actorId.trim() !== '' ||
    applied.from !== '' ||
    applied.to !== '';

  const submit = () => {
    setApplied(draft);
    setPage(1);
    setExpandedId(null);
  };

  const reset = () => {
    setDraft(EMPTY_FILTER);
    setApplied(EMPTY_FILTER);
    setPage(1);
    setExpandedId(null);
  };

  const patch = (key: keyof FilterDraft) => (value: string) =>
    setDraft((d) => ({ ...d, [key]: value }));

  const chain = verify.data;
  const chainPending = verify.isLoading || verify.isFetching;

  return (
    <div className="auditpg">
      {/* ── ⭐ 哈希链校验状态 ── */}
      <Panel
        className="auditpg__chain"
        title="哈希链完整性"
        extra={
          <Button
            variant="ghost"
            size="sm"
            data-testid="audit-chain-verify"
            onClick={() => verify.refetch()}
            disabled={chainPending}
          >
            {chainPending ? '校验中…' : '重新校验'}
          </Button>
        }
      >
        <div className="auditpg__chain-row" data-testid="audit-chain-status">
          {verify.isError ? (
            <>
              <Badge tone="alert">校验失败</Badge>
              <span className="auditpg__chain-text">
                无法连接操作日志校验服务：{(verify.error as Error)?.message ?? '未知错误'}
              </span>
            </>
          ) : chainPending && !chain ? (
            <>
              <Badge tone="idle">校验中</Badge>
              <span className="auditpg__chain-text">正在逐条重算全量操作日志哈希链…</span>
            </>
          ) : chain ? (
            <>
              <Badge tone={chain.intact ? 'ok' : 'alert'}>
                {chain.intact ? '链完好' : `${chain.brokenCount} 条断链`}
              </Badge>
              <span className="auditpg__chain-text">
                {chain.intact
                  ? '全部操作记录的哈希链连续，数据未被篡改。'
                  : '以下记录的 prev_hash 与前一条不符，可能存在篡改或数据合并，请核查。'}
              </span>
            </>
          ) : null}
        </div>

        {chain && !chain.intact && chain.broken.length > 0 && (
          <details className="auditpg__broken" open>
            <summary>断链明细（{chain.brokenCount}）</summary>
            <ul className="auditpg__broken-list">
              {chain.broken.slice(0, 30).map((b) => (
                <li key={b.brokenId}>
                  <span className="auditpg__broken-id num">#{b.brokenId}</span>
                  <span className="auditpg__broken-reason">{b.reason}</span>
                </li>
              ))}
            </ul>
          </details>
        )}
      </Panel>

      {/* ── 筛选 ── */}
      <Panel className="auditpg__panel" flush>
        <form
          className="auditpg__filters"
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <label className="auditpg__field">
            <span>动作</span>
            <select
              data-testid="audit-filter-action"
              value={draft.action}
              onChange={(e) => patch('action')(e.target.value)}
            >
              <option value="">全部动作</option>
              {ACTION_OPTIONS.map((a) => (
                <option key={a} value={a}>
                  {actionLabel(a)}
                </option>
              ))}
            </select>
          </label>

          <label className="auditpg__field">
            <span>资源类型</span>
            <select
              data-testid="audit-filter-resourceType"
              value={draft.resourceType}
              onChange={(e) => patch('resourceType')(e.target.value)}
            >
              <option value="">全部类型</option>
              {RESOURCE_TYPE_OPTIONS.map((t) => (
                <option key={t} value={t}>
                  {resourceLabel(t)}
                </option>
              ))}
            </select>
          </label>

          <label className="auditpg__field">
            <span>操作人 ID</span>
            <input
              data-testid="audit-filter-actorId"
              inputMode="numeric"
              placeholder="如 2"
              value={draft.actorId}
              onChange={(e) => patch('actorId')(e.target.value.replace(/[^\d]/g, ''))}
            />
          </label>

          <label className="auditpg__field">
            <span>开始日期</span>
            <input
              data-testid="audit-filter-from"
              type="date"
              value={draft.from}
              onChange={(e) => patch('from')(e.target.value)}
            />
          </label>

          <label className="auditpg__field">
            <span>结束日期</span>
            <input
              data-testid="audit-filter-to"
              type="date"
              value={draft.to}
              onChange={(e) => patch('to')(e.target.value)}
            />
          </label>

          <div className="auditpg__filter-actions">
            <Button type="submit" variant="primary" size="sm" data-testid="audit-search">
              查询
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              data-testid="audit-reset"
              onClick={reset}
            >
              重置
            </Button>
          </div>
        </form>

        {/* ── 列表 ── */}
        {logs.isError ? (
          <div className="auditpg__error" data-testid="audit-error">
            加载操作流失败：{(logs.error as Error)?.message ?? '未知错误'}
          </div>
        ) : logs.isLoading ? (
          <div className="auditpg__empty">加载中…</div>
        ) : rows.length === 0 ? (
          <div className="auditpg__empty" data-testid="audit-empty">
            {hasFilter ? '没有符合筛选条件的操作记录' : '暂无操作记录'}
          </div>
        ) : (
          <div className="auditpg__table-wrap">
            <table className="auditpg__table" data-testid="audit-table">
              <thead>
                <tr>
                  <th>时间</th>
                  <th>操作人</th>
                  <th>动作</th>
                  <th>资源</th>
                  <th>模式</th>
                  <th>摘要</th>
                  <th>哈希</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const mode = modeLabel(r.mode);
                  const diffText = renderDiff(r.diff);
                  const isOpen = expandedId === r.id;
                  return (
                    <FragmentRow
                      key={r.id}
                      record={r}
                      mode={mode}
                      diffText={diffText}
                      isOpen={isOpen}
                      onToggle={() => setExpandedId((id) => (id === r.id ? null : r.id))}
                    />
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* ── 分页 ── */}
        <div className="auditpg__pager">
          <span className="auditpg__pager-info">
            第 <b className="num">{page}</b> 页 · 本页 <b className="num">{rows.length}</b> 条
            {!logs.isLoading && !hasNext && ' · 已到末页'}
          </span>
          <div className="auditpg__pager-btns">
            <Button
              variant="ghost"
              size="sm"
              data-testid="audit-prev"
              disabled={page <= 1 || logs.isFetching}
              onClick={() => {
                setPage((p) => Math.max(1, p - 1));
                setExpandedId(null);
              }}
            >
              上一页
            </Button>
            <Button
              variant="ghost"
              size="sm"
              data-testid="audit-next"
              disabled={!hasNext || logs.isFetching}
              onClick={() => {
                setPage((p) => p + 1);
                setExpandedId(null);
              }}
            >
              下一页
            </Button>
          </div>
        </div>
      </Panel>
    </div>
  );
}

/* ── 行 + 展开明细 ── */

interface RowProps {
  record: AuditLogRecord;
  mode: string;
  diffText: string;
  isOpen: boolean;
  onToggle: () => void;
}

function FragmentRow({ record: r, mode, diffText, isOpen, onToggle }: RowProps) {
  return (
    <>
      <tr
        className="auditpg__row"
        data-testid="audit-row"
        data-resource-type={(r.resource_type ?? '').toLowerCase()}
        data-action={r.action}
        data-actor-id={r.actor_id ?? ''}
        onClick={onToggle}
      >
        <td className="auditpg__time num">{fmtDateTime(r.created_at)}</td>
        <td className="auditpg__actor">
          {r.actor_name ?? (r.actor_id ? `#${r.actor_id}` : '系统')}
          {r.actor_role && <span className="auditpg__role">{r.actor_role}</span>}
        </td>
        <td>
          <span className="auditpg__action-label">{actionLabel(r.action)}</span>
          <span className="auditpg__action-code">{r.action}</span>
        </td>
        <td className="auditpg__resource">
          <span className="auditpg__rtype">{resourceLabel(r.resource_type)}</span>
          {r.resource_id && <span className="auditpg__rid num">#{r.resource_id}</span>}
        </td>
        <td>{mode && <span className="auditpg__mode">{mode}</span>}</td>
        <td className="auditpg__summary">{diffText || r.reason || '—'}</td>
        <td className="auditpg__hash num" title={r.row_hash ?? ''}>
          {shortHash(r.row_hash)}
        </td>
      </tr>
      {isOpen && (
        <tr className="auditpg__detail-row">
          <td colSpan={7}>
            <div className="auditpg__detail" data-testid="audit-detail">
              <dl>
                <dt>操作 ID</dt>
                <dd className="num">{r.id}</dd>
                <dt>操作人</dt>
                <dd>
                  {r.actor_name ?? '—'}
                  {r.actor_id ? ` (ID ${r.actor_id})` : ''}
                  {r.actor_role ? ` · ${r.actor_role}` : ''}
                </dd>
                <dt>资源</dt>
                <dd>
                  {resourceLabel(r.resource_type)}
                  {r.resource_id ? ` #${r.resource_id}` : ''}
                </dd>
                <dt>项目</dt>
                <dd className="num">{r.project_id ?? '—'}</dd>
                <dt>时间</dt>
                <dd className="num">{fmtDateTime(r.created_at)}</dd>
                {r.reason && (
                  <>
                    <dt>变更理由</dt>
                    <dd>{r.reason}</dd>
                  </>
                )}
                {r.diff && (
                  <>
                    <dt>变更明细</dt>
                    <dd>
                      <pre className="auditpg__json">{JSON.stringify(r.diff, null, 2)}</pre>
                    </dd>
                  </>
                )}
                {(r.before_value != null || r.after_value != null) && (
                  <>
                    <dt>前后值</dt>
                    <dd>
                      <pre className="auditpg__json">
                        {`before: ${JSON.stringify(r.before_value, null, 2)}\nafter: ${JSON.stringify(
                          r.after_value,
                          null,
                          2,
                        )}`}
                      </pre>
                    </dd>
                  </>
                )}
                <dt>row_hash</dt>
                <dd className="auditpg__hash-full num">{r.row_hash ?? '—'}</dd>
                <dt>prev_hash</dt>
                <dd className="auditpg__hash-full num">{r.prev_hash ?? '—'}</dd>
              </dl>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
